import {useEffect, useRef, useState} from 'react'
import {Modal, Button, Form, Row, Col, Spinner, Nav} from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import Select from 'react-select'
import {PolicyFilters, PolicySavedFilter} from '../policiesTypes'
import {DateInput} from 'app/tfi/components/DateInput'
import { MultiSelectWithActions } from 'app/tfi/components/MultiSelectWithActions'
import {parseId} from '../../../helpers'
import {INITIAL_POLICY_FILTERS} from '../../../helpers/consts'
import {
  documentStatusLabel,
  immigrationStatusLabel,
  paymentStatusLabel,
  translatedGenders,
  translatedMetalLevels,
} from '../../../helpers/catalogMessages'

const M = defineMessages({
  // Header / tabs / actions
  title: {id: 'policies.list.filters.title', defaultMessage: 'Advanced Filters'},
  loadingOptions: {id: 'policies.list.filters.loadingOptions', defaultMessage: 'Loading filter options...'},
  tabFilters: {id: 'policies.list.filters.tab.filters', defaultMessage: 'Filters'},
  tabSaved: {id: 'policies.list.filters.tab.saved', defaultMessage: 'Saved filters'},
  clear: {id: 'policies.list.filters.action.clear', defaultMessage: 'Clear'},
  saveFilter: {id: 'policies.list.filters.action.saveFilter', defaultMessage: 'Save Filter'},
  applyFilters: {id: 'policies.list.filters.action.applyFilters', defaultMessage: 'Apply Filters'},
  manageSaved: {id: 'policies.list.filters.saved.manage', defaultMessage: 'Manage saved filters'},
  manageHint: {id: 'policies.list.filters.saved.manageHint', defaultMessage: 'Select a saved filter for quick access to data'},
  filterName: {id: 'policies.list.filters.saved.filterName', defaultMessage: 'Filter name'},
  saveHint: {id: 'policies.list.filters.saved.saveHint', defaultMessage: 'Save the current filters so you can reuse them later.'},
  saveAndApply: {id: 'policies.list.filters.saved.saveAndApply', defaultMessage: 'Save and apply'},
  savedFilters: {id: 'policies.list.filters.saved.label', defaultMessage: 'Saved Filters'},
  delete: {id: 'policies.list.filters.action.delete', defaultMessage: 'Delete'},
  applySaved: {id: 'policies.list.filters.saved.apply', defaultMessage: 'Apply saved filter'},
  confirmDelete: {id: 'policies.list.filters.saved.confirmDelete', defaultMessage: 'Delete this saved filter?'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  // Field labels
  fAgent: {id: 'policies.list.filters.field.agent', defaultMessage: 'Agent'},
  fAssignedTo: {id: 'policies.list.filters.field.assignedTo', defaultMessage: 'Assigned To'},
  fCarrier: {id: 'policies.list.filters.field.carrier', defaultMessage: 'Carrier'},
  fState: {id: 'policies.list.filters.field.state', defaultMessage: 'State'},
  fStateJurisdictionType: {id: 'policies.list.filters.field.stateJurisdictionType', defaultMessage: 'State Jurisdiction Type'},
  fZipCode: {id: 'policies.list.filters.field.zipCode', defaultMessage: 'Zip Code'},
  fActiveMonths: {
    id: 'policies.list.filters.field.activeMonths',
    defaultMessage: 'Active time (months)',
  },
  phActiveMin: {id: 'policies.list.filters.ph.activeMin', defaultMessage: 'From'},
  phActiveMax: {id: 'policies.list.filters.ph.activeMax', defaultMessage: 'To'},
  activeMonthsHelp: {
    id: 'policies.list.filters.help.activeMonths',
    defaultMessage: 'Months of real coverage, adding up terms and skipping time canceled.',
  },
  fImmigrationStatus: {id: 'policies.list.filters.field.immigrationStatus', defaultMessage: 'Immigration Status'},
  fPolicyStatus: {id: 'policies.list.filters.field.policyStatus', defaultMessage: 'Policy Status'},
  fDocumentStatus: {id: 'policies.list.filters.field.documentStatus', defaultMessage: 'Document Status'},
  fPaymentStatus: {id: 'policies.list.filters.field.paymentStatus', defaultMessage: 'Payment status'},
  fPolicyTypes: {id: 'policies.list.filters.field.policyTypes', defaultMessage: 'Policy Types'},
  fUnassignedPolicies: {id: 'policies.list.filters.field.unassignedPolicies', defaultMessage: 'Unassigned Policies'},
  fSource: {id: 'policies.list.filters.field.source', defaultMessage: 'Source'},
  sourceDisabled: {id: 'policies.list.filters.source.disabled', defaultMessage: '{name} (disabled)'},
  fMetalLevel: {id: 'policies.list.filters.field.metalLevel', defaultMessage: 'Metal Level'},
  fGenderHolder: {id: 'policies.list.filters.field.genderHolder', defaultMessage: 'Gender (Holder)'},
  fMemberIdStatus: {id: 'policies.list.filters.field.memberIdStatus', defaultMessage: 'Member ID Status'},
  fCommissionReportStatus: {id: 'policies.list.filters.field.commissionReportStatus', defaultMessage: 'Commission Report Status'},
  fActiveAcaCoverage: {id: 'policies.list.filters.field.activeAcaCoverage', defaultMessage: 'Active ACA coverage'},
  fCommissionPaymentStatus: {id: 'policies.list.filters.field.commissionPaymentStatus', defaultMessage: 'Commission Payment Status'},
  fOwnsCar: {id: 'policies.list.filters.field.ownsCar', defaultMessage: 'Owns car'},
  fOwnsHome: {id: 'policies.list.filters.field.ownsHome', defaultMessage: 'Owns home'},
  fHasAutoPolicy: {id: 'policies.list.filters.field.hasAutoPolicy', defaultMessage: 'Has auto policy'},
  fHasHomePolicy: {id: 'policies.list.filters.field.hasHomePolicy', defaultMessage: 'Has home policy'},
  fHasCarrierAccount: {id: 'policies.list.filters.field.hasCarrierAccount', defaultMessage: 'Has carrier account'},
  fAccountCreatedByClient: {id: 'policies.list.filters.field.accountCreatedByClient', defaultMessage: 'Account created by client'},
  fSignatureFormStatus: {id: 'policies.list.filters.field.signatureFormStatus', defaultMessage: 'Signature Form Status'},
  fSignatureForm: {id: 'policies.list.filters.field.signatureForm', defaultMessage: 'Signature Form'},
  fAgentNpn: {id: 'policies.list.filters.field.agentNpn', defaultMessage: 'Agent NPN in Signature Form'},
  fCarrierStatus: {id: 'policies.list.filters.field.carrierStatus', defaultMessage: 'Carrier Status'},
  fAgeIn: {id: 'policies.list.filters.field.ageIn', defaultMessage: 'Age-In'},
  fDelinquent: {id: 'policies.list.filters.field.delinquent', defaultMessage: 'Delinquent'},
  fUnpaidPending: {id: 'policies.list.filters.field.unpaidPending', defaultMessage: 'Unpaid Pending'},
  fUnpaidPastDue: {id: 'policies.list.filters.field.unpaidPastDue', defaultMessage: 'Unpaid Past Due'},
  fNoCarrierImport: {id: 'policies.list.filters.field.noCarrierImport', defaultMessage: 'No Carrier Import'},
  fAcaResult: {id: 'policies.list.filters.field.acaResult', defaultMessage: 'ACA Result'},
  fAcaValidity: {id: 'policies.list.filters.field.acaValidity', defaultMessage: 'ACA Validity'},
  fAdvisorPaymentStatus: {id: 'policies.list.filters.field.advisorPaymentStatus', defaultMessage: 'Advisor Payment Status'},
  fChargebackStatus: {id: 'policies.list.filters.field.chargebackStatus', defaultMessage: 'Chargeback Status'},
  // Section headings
  hSignatureForm: {id: 'policies.list.filters.section.signatureForm', defaultMessage: 'Signature Form Filters'},
  hCarrierImport: {id: 'policies.list.filters.section.carrierImport', defaultMessage: 'Carrier Import Filters'},
  hAcaVerification: {id: 'policies.list.filters.section.acaVerification', defaultMessage: 'ACA Verification Filters'},
  hAdvisorPayment: {id: 'policies.list.filters.section.advisorPayment', defaultMessage: 'Advisor Payment Filters'},
  // DateInput labels
  dEffectiveFrom: {id: 'policies.list.filters.date.effectiveFrom', defaultMessage: 'Effective Date From'},
  dEffectiveTo: {id: 'policies.list.filters.date.effectiveTo', defaultMessage: 'Effective Date To'},
  dCreatedFrom: {id: 'policies.list.filters.date.createdFrom', defaultMessage: 'Created Date From'},
  dCreatedTo: {id: 'policies.list.filters.date.createdTo', defaultMessage: 'Created Date To'},
  dPaymentStatusFrom: {id: 'policies.list.filters.date.paymentStatusFrom', defaultMessage: 'Payment Status Date From'},
  dPaymentStatusTo: {id: 'policies.list.filters.date.paymentStatusTo', defaultMessage: 'Payment Status Date To'},
  dCancellationFrom: {id: 'policies.list.filters.date.cancellationFrom', defaultMessage: 'Cancellation Date From'},
  dCancellationTo: {id: 'policies.list.filters.date.cancellationTo', defaultMessage: 'Cancellation Date To'},
  dDocumentsDueFrom: {id: 'policies.list.filters.date.documentsDueFrom', defaultMessage: 'Documents Due Date From'},
  dDocumentsDueTo: {id: 'policies.list.filters.date.documentsDueTo', defaultMessage: 'Documents Due Date To'},
  dCarrierAsOfFrom: {id: 'policies.list.filters.date.carrierAsOfFrom', defaultMessage: 'Carrier As-of Date From'},
  dCarrierAsOfTo: {id: 'policies.list.filters.date.carrierAsOfTo', defaultMessage: 'Carrier As-of Date To'},
  dAdvisorPaidFrom: {id: 'policies.list.filters.date.advisorPaidFrom', defaultMessage: 'Advisor Paid Date From'},
  dAdvisorPaidTo: {id: 'policies.list.filters.date.advisorPaidTo', defaultMessage: 'Advisor Paid Date To'},
  dChargebackFrom: {id: 'policies.list.filters.date.chargebackFrom', defaultMessage: 'Chargeback Date From'},
  dChargebackTo: {id: 'policies.list.filters.date.chargebackTo', defaultMessage: 'Chargeback Date To'},
  // Placeholders
  phFilterName: {id: 'policies.list.filters.placeholder.filterName', defaultMessage: 'Enter filter name'},
  phSelectSavedFilter: {id: 'policies.list.filters.placeholder.selectSavedFilter', defaultMessage: 'Select saved filter'},
  phSelectJurisdiction: {id: 'policies.list.filters.placeholder.selectJurisdiction', defaultMessage: 'Select jurisdiction type'},
  phSelectMetalLevel: {id: 'policies.list.filters.placeholder.selectMetalLevel', defaultMessage: 'Select Metal Level'},
  phSelectCarrierStatus: {id: 'policies.list.filters.placeholder.selectCarrierStatus', defaultMessage: 'Select carrier status...'},
  phAny: {id: 'policies.list.filters.placeholder.any', defaultMessage: 'Any'},
  phAnyResult: {id: 'policies.list.filters.placeholder.anyResult', defaultMessage: 'Any result...'},
  phAnyStatus: {id: 'policies.list.filters.placeholder.anyStatus', defaultMessage: 'Any status...'},
  phSelectOption: {id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'},
  // Option labels
  optAllPolicies: {id: 'policies.list.filters.opt.allPolicies', defaultMessage: 'All policies'},
  optPaid: {id: 'policies.list.filters.opt.paid', defaultMessage: 'Paid'},
  optNotPaid: {id: 'policies.list.filters.opt.notPaid', defaultMessage: 'Not paid'},
  optChargeback: {id: 'policies.list.filters.opt.chargeback', defaultMessage: 'Chargeback'},
  optNoChargeback: {id: 'policies.list.filters.opt.noChargeback', defaultMessage: 'No chargeback'},
  optSigned: {id: 'policies.list.filters.opt.signed', defaultMessage: 'Signed'},
  optNotSigned: {id: 'policies.list.filters.opt.notSigned', defaultMessage: 'Not Signed'},
  optAllStates: {id: 'policies.list.filters.opt.allStates', defaultMessage: 'All states'},
  optFederal: {id: 'policies.list.filters.opt.federal', defaultMessage: 'Federal'},
  optStateJur: {id: 'policies.list.filters.opt.stateJurisdiction', defaultMessage: 'State'},
  optOnlyUnassigned: {id: 'policies.list.filters.opt.onlyUnassigned', defaultMessage: 'Only unassigned'},
  optOnlyAssigned: {id: 'policies.list.filters.opt.onlyAssigned', defaultMessage: 'Only assigned'},
  optWithMemberId: {id: 'policies.list.filters.opt.withMemberId', defaultMessage: 'With Member ID'},
  optWithoutMemberId: {id: 'policies.list.filters.opt.withoutMemberId', defaultMessage: 'Without Member ID'},
  optConsideredCommission: {id: 'policies.list.filters.opt.consideredCommission', defaultMessage: 'Considered for commission'},
  optNotConsideredCommission: {id: 'policies.list.filters.opt.notConsideredCommission', defaultMessage: 'Not considered for commission'},
  optWithoutActiveAca: {id: 'policies.list.filters.opt.withoutActiveAca', defaultMessage: 'Without an active ACA policy'},
  optWithActiveAca: {id: 'policies.list.filters.opt.withActiveAca', defaultMessage: 'With an active ACA policy'},
  segNeverStarted: {id: 'policies.list.filters.seg.neverStarted', defaultMessage: 'Never Started'},
  segCanceled: {id: 'policies.list.filters.seg.canceled', defaultMessage: 'Canceled'},
  segSinComision: {id: 'policies.list.filters.seg.sinComision', defaultMessage: 'No Commission'},
  segStandBy: {id: 'policies.list.filters.seg.standBy', defaultMessage: 'Stand By'},
  csInForce: {id: 'policies.list.filters.cs.inForce', defaultMessage: 'In Force'},
  csTerminated: {id: 'policies.list.filters.cs.terminated', defaultMessage: 'Terminated'},
  csGracePeriod: {id: 'policies.list.filters.cs.gracePeriod', defaultMessage: 'Grace Period'},
  csPendingEffectuation: {id: 'policies.list.filters.cs.pendingEffectuation', defaultMessage: 'Pending Effectuation'},
  csPastDue: {id: 'policies.list.filters.cs.pastDue', defaultMessage: 'Past Due'},
  csDelinquent: {id: 'policies.list.filters.cs.delinquent', defaultMessage: 'Delinquent'},
  csFutureEffective: {id: 'policies.list.filters.cs.futureEffective', defaultMessage: 'Future Effective'},
  csUnknown: {id: 'policies.list.filters.cs.unknown', defaultMessage: 'Unknown'},
  acaGreen: {id: 'policies.list.filters.aca.green', defaultMessage: 'Paid & Completed'},
  acaYellow: {id: 'policies.list.filters.aca.yellow', defaultMessage: 'Paid / Pending Documents'},
  acaRed: {id: 'policies.list.filters.aca.red', defaultMessage: 'Unpaid / Pending'},
  acaBlue: {id: 'policies.list.filters.aca.blue', defaultMessage: '$0 Premium / Completed'},
  acaVal28: {id: 'policies.list.filters.acaVal.d28', defaultMessage: '≤ 28 days left (any active)'},
  acaVal21: {id: 'policies.list.filters.acaVal.d21', defaultMessage: '≤ 21 days left'},
  acaVal14: {id: 'policies.list.filters.acaVal.d14', defaultMessage: '≤ 14 days left'},
  acaVal7: {id: 'policies.list.filters.acaVal.d7', defaultMessage: '≤ 7 days left'},
  acaVal3: {id: 'policies.list.filters.acaVal.d3', defaultMessage: '≤ 3 days left'},
  acaValNone: {id: 'policies.list.filters.acaVal.none', defaultMessage: 'No active verification'},
  boolYes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  boolNo: {id: 'common.boolean.no', defaultMessage: 'No'},
})

interface ModalFiltersProps {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: any) => void
  setFilters: React.Dispatch<React.SetStateAction<PolicyFilters>>
  filters: PolicyFilters
  savedFilters?: PolicySavedFilter[]
  onSaveFilter?: (name: string, filters: PolicyFilters) => Promise<PolicySavedFilter | null>
  onUpdateFilter?: (id: string, name: string, filters: PolicyFilters) => Promise<PolicySavedFilter | null>
  onDeleteFilter?: (id: string) => Promise<boolean>
  users: any[]
  agents: any[]
  states: any[]
  immigrationStatuses: any[]
  signatureForms: any[]
  carriers: any[]
  policyStatuses: any[]
  documentStatuses: any[]
  paymentStatuses: any[]
  leadSources: any[]
  policyTypes: any[]
  isLoading?: boolean
  isAgent?: boolean
}

export const ModalFilters: React.FC<ModalFiltersProps> = ({
  isOpen,
  onClose,
  onApply,
  users,
  agents,
  states,
  immigrationStatuses,
  signatureForms,
  carriers,
  policyStatuses,
  documentStatuses,
  paymentStatuses,
  leadSources,
  policyTypes,
  setFilters,
  filters,
  savedFilters = [],
  onSaveFilter,
  onDeleteFilter,
  isLoading = false,
  isAgent = false,
}) => {
  const intl = useIntl()
  const yesLabel = intl.formatMessage(M.boolYes)
  const noLabel = intl.formatMessage(M.boolNo)
  const allPoliciesLabel = intl.formatMessage(M.optAllPolicies)
  // Enums del frontend: la etiqueta se traduce en `helpers/catalogMessages`, que
  // es el unico sitio donde viven estos descriptores (slice 1.11.c.2.b).
  const genderOptions = translatedGenders(intl)
  // Translated label for a carrier status code (selected-value display).
  const carrierStatusLabel = (v: string) => {
    const byCode: Record<string, {id: string; defaultMessage: string}> = {
      IN_FORCE: M.csInForce,
      TERMINATED: M.csTerminated,
      GRACE_PERIOD: M.csGracePeriod,
      PENDING_EFFECTUATION: M.csPendingEffectuation,
      PAST_DUE: M.csPastDue,
      DELINQUENT: M.csDelinquent,
      FUTURE_EFFECTIVE: M.csFutureEffective,
      UNKNOWN: M.csUnknown,
    }
    return byCode[v] ? intl.formatMessage(byCode[v]) : v.replace(/_/g, ' ')
  }
  const [activeTab, setActiveTab] = useState<'filters' | 'saved'>('filters')
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null)
  const [savedFilterName, setSavedFilterName] = useState('')
  const [shouldFocusSavedName, setShouldFocusSavedName] = useState(false)
  const [savedTabMode, setSavedTabMode] = useState<'save' | 'manage'>('manage')
  const savedNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (!selectedSavedFilterId) {
      setSavedFilterName('')
    }
  }, [isOpen, selectedSavedFilterId])

  useEffect(() => {
    if (activeTab !== 'saved' || !shouldFocusSavedName) return
    if (savedNameInputRef.current) {
      savedNameInputRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
      savedNameInputRef.current.focus()
      savedNameInputRef.current.select()
      setShouldFocusSavedName(false)
    }
  }, [activeTab, shouldFocusSavedName])

  const goToSavedTab = (mode: 'save' | 'manage') => {
    setActiveTab('saved')
    setSavedTabMode(mode)
    setShouldFocusSavedName(mode === 'save')
  }

  const handleSelectChange = (field: string, selected: any) => {
    console.log('field:', field)
    console.log('value:', selected)
    const isArray = Array.isArray(selected)
    let value: any = selected

    if (isArray) {
      value = selected.map((opt: any) => opt?.value).filter((v: any) => v !== '')
    } else {
      value = selected?.value
    }

    // Solo campos booleanos (ajusta esta lista según tus filtros booleanos)
    const booleanFields = [
      'isImportant',
      'isArchived',
      'isPolicyHolder',
      'withoutPolicy',
      'withoutLead',
      'myContacts',
      'hasMemberId',
      'commissionPayable',
      'withoutActiveAcaPolicy',
      'advisorPaid',
      'advisorChargeback',
      'unassigned',
      'ownsCar',
      'ownsHome',
      'hasAutoPolicy',
      'hasHomePolicy',
      'hasCarrierAccount',
      'accountCreatedByClient',
      'carrierIsAgeIn',
      'carrierIsDelinquent',
      'carrierIsUnpaidPending',
      'carrierIsUnpaidPastDue',
      'carrierNoImportData',
    ]

    if (!isArray) {
      if (booleanFields.includes(field)) {
        if (value === 'true') value = true
        else if (value === 'false') value = false
        else if (value === '') value = undefined
      } else if (value === '') {
        value = undefined
      }
    }

    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
    console.log('filters:', filters)
  }

  const handleInputChange = (field: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  /**
   * El filtro de tiempo activo viaja como Float al backend, no como string. Vaciar el input
   * tiene que borrar la clave (undefined) y no mandar 0, que es un filtro distinto: 0 son
   * las pólizas que nunca llegaron a estar activas.
   */
  const handleActiveMonthsChange = (field: 'activeMonthsMin' | 'activeMonthsMax', value: string) => {
    const limpio = value.trim()
    const numero = limpio === '' ? undefined : Number(limpio)
    setFilters((prev: any) => ({
      ...prev,
      [field]: numero !== undefined && Number.isFinite(numero) && numero >= 0 ? numero : undefined,
    }))
  }

  const handleImmigrationStatusChange = (selected: any) => {
    const values = Array.isArray(selected)
      ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
      : selected?.value
        ? [String(selected.value).trim()].filter(Boolean)
        : []

    setFilters((prev: any) => ({
      ...prev,
      immigrationStatus: values,
    }))
  }

  /**
   * Normaliza un filtro numérico a `number | undefined`.
   *
   * No sirve `Number(v)` a secas porque `Number('')` es 0, y 0 es un filtro con significado
   * propio acá (las pólizas que nunca llegaron a estar activas). Los filtros se persisten en
   * localStorage, así que el valor puede volver como string.
   */
  const toOptionalNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    return undefined
  }

  const handleSubmit = () => {
    const activeMonthsMinValue = toOptionalNumber(filters.activeMonthsMin)
    const activeMonthsMaxValue = toOptionalNumber(filters.activeMonthsMax)
    const parsedFilters = {
      ...filters,
      ...(filters.commissionPaymentStatus && {
        commissionPaymentStatus: filters.commissionPaymentStatus,
      }),
      ...(filters.signatureForm && {signatureForm: filters.signatureForm}),
      ...(filters.policyType && {policyType: filters.policyType}),
      ...(filters.leadSource && {leadSource: filters.leadSource}),
      ...(filters.user && {user: filters.user}),
      ...(filters.carrier && {carrier: filters.carrier}), // array ok
      ...(filters.assignedTo && {assignedTo: filters.assignedTo}), // array ok
      ...(filters.stateEU && {stateEU: filters.stateEU}), // array ok
      ...(filters.stateJurisdictionType && {stateJurisdictionType: filters.stateJurisdictionType}),
      ...(filters.status && {status: filters.status}), // array ok
      ...(filters.immigrationStatus && {immigrationStatus: filters.immigrationStatus}),
      ...(filters.holderGender && {holderGender: filters.holderGender}),
      ...(filters.documentStatus && {documentStatus: filters.documentStatus}),
      ...(filters.paymentStatus && {paymentStatus: filters.paymentStatus}),
      ...(filters.metalLevel && {metalLevel: filters.metalLevel}),
      // 0 es un filtro válido (las pólizas que nunca llegaron a estar activas), así que no
      // se puede filtrar por truthy. Y '' tiene que quedar afuera: el argumento es Float y
      // la query falla. Ojo que `Number('')` es 0, de ahí el helper en vez de una coerción.
      ...(activeMonthsMinValue !== undefined && {activeMonthsMin: activeMonthsMinValue}),
      ...(activeMonthsMaxValue !== undefined && {activeMonthsMax: activeMonthsMaxValue}),
      ...(filters.advisorPaid !== undefined && {advisorPaid: filters.advisorPaid}),
      ...(filters.advisorChargeback !== undefined && {advisorChargeback: filters.advisorChargeback}),
      ...(filters.unassigned !== undefined && {unassigned: filters.unassigned}),
      ...(filters.withoutActiveAcaPolicy !== undefined && {withoutActiveAcaPolicy: filters.withoutActiveAcaPolicy}),
      ...(filters.ownsCar !== undefined && {ownsCar: filters.ownsCar}),
      ...(filters.ownsHome !== undefined && {ownsHome: filters.ownsHome}),
      ...(filters.hasAutoPolicy !== undefined && {hasAutoPolicy: filters.hasAutoPolicy}),
      ...(filters.hasHomePolicy !== undefined && {hasHomePolicy: filters.hasHomePolicy}),
      ...(filters.hasCarrierAccount !== undefined && {hasCarrierAccount: filters.hasCarrierAccount}),
      ...(filters.accountCreatedByClient !== undefined && {accountCreatedByClient: filters.accountCreatedByClient}),
      ...(filters.paymentStatusDateAfter && {paymentStatusDateAfter: filters.paymentStatusDateAfter}),
      ...(filters.paymentStatusDateBefore && {paymentStatusDateBefore: filters.paymentStatusDateBefore}),
      ...(filters.documentsDueDateAfter && {documentsDueDateAfter: filters.documentsDueDateAfter}),
      ...(filters.documentsDueDateBefore && {documentsDueDateBefore: filters.documentsDueDateBefore}),
      ...(filters.advisorPaidDateAfter && {advisorPaidDateAfter: filters.advisorPaidDateAfter}),
      ...(filters.advisorPaidDateBefore && {advisorPaidDateBefore: filters.advisorPaidDateBefore}),
      ...(filters.advisorChargebackDateAfter && {advisorChargebackDateAfter: filters.advisorChargebackDateAfter}),
      ...(filters.advisorChargebackDateBefore && {advisorChargebackDateBefore: filters.advisorChargebackDateBefore}),
      ...(filters.acaResult && filters.acaResult.length > 0 && {acaResult: filters.acaResult}),
      ...(filters.acaValidity && {acaValidity: filters.acaValidity}),
    }
    console.log('🚀 Filtros aplicados (parseados) desde el modal:', parsedFilters)
    onApply(parsedFilters)
    onClose()
  }

  const toArray = (value: unknown) => {
    if (Array.isArray(value)) return value
    if (typeof value === 'string') return value !== '' ? [value] : []
    return value != null ? [value] : []
  }

  const agentsOptions = [
    ...agents.map((u) => ({
      value: parseId(u.id),
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })),
  ]

  const userValues = toArray(filters.user).map(String)
  const selectedAgentsOptions = userValues.length
    ? agentsOptions.filter((opt) => userValues.includes(String(opt.value)))
    : null

  const assignedToOptions = [
    ...users.map((u) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })),
  ]

  const assignedToValues = toArray(filters.assignedTo).map(String)
  const selectedAsignedToOptions = assignedToValues.length
    ? assignedToOptions.filter((opt) => assignedToValues.includes(String(opt.value)))
    : null

  const stateEUOptions = [
    ...states.map((s) => ({
      value: s.code,
      label: `${s.name} (${s.code})`,
    })),
  ]

  const stateEUValues = toArray(filters.stateEU).map(String)
  const selectedStateEUOptions = stateEUValues.length
    ? stateEUOptions.filter((opt) => stateEUValues.includes(String(opt.value)))
    : null

  const carrierOptions = [
    ...carriers.map((s) => ({
      value: s.id,
      label: s.name,
    })),
  ]

  const carrierValues = toArray(filters.carrier).map(String)
  const selectedCarrierOptions = carrierValues.length
    ? carrierOptions.filter((opt) => carrierValues.includes(String(opt.value)))
    : null

  // Group policy statuses by category
  const policyStatusGrouped: {[key: string]: any[]} = {}
  policyStatuses.forEach((s) => {
    const category = s.category || 'OTHER'
    if (!policyStatusGrouped[category]) {
      policyStatusGrouped[category] = []
    }
    policyStatusGrouped[category].push({
      value: String(s.code),
      label: s.title,
    })
  })

  const policyStatusOptions = Object.keys(policyStatusGrouped).map(category => ({
    label: category.replace(/_/g, ' '),
    options: policyStatusGrouped[category]
  }))

  // Flatten for selection matching
  const allPolicyStatusOptions = policyStatusOptions.flatMap(cat => cat.options || [])

  const statusValues = toArray(filters.status).map(String)
  const selectedPolicyStatusOptions = statusValues.length
    ? allPolicyStatusOptions.filter((opt) => statusValues.includes(String(opt.value)))
    : null

  const documentStatusOptions = documentStatuses.map((s) => ({
    value: String(s.value),
    label: documentStatusLabel(intl, String(s.value), s.label),
  }))

  const documentStatusValues = toArray(filters.documentStatus).map(String)
  const selectedDocumentStatusOptions = documentStatusValues.length
    ? documentStatusOptions.filter((opt) => documentStatusValues.includes(String(opt.value)))
    : null

  const payentStatusOptions = paymentStatuses.map((s) => ({
    value: String(s.value),
    label: paymentStatusLabel(intl, String(s.value), s.label),
  }))

  const paymentStatusValues = toArray(filters.paymentStatus).map(String)
  const selectedPaymentStatusOptions = paymentStatusValues.length
    ? payentStatusOptions.filter((opt) => paymentStatusValues.includes(String(opt.value)))
    : null

  const yesNoOptions = [
    {value: '', label: allPoliciesLabel},
    {value: 'true', label: yesLabel},
    {value: 'false', label: noLabel},
  ]

  const advisorPaidOptions = [
    {value: '', label: allPoliciesLabel},
    {value: 'true', label: intl.formatMessage(M.optPaid)},
    {value: 'false', label: intl.formatMessage(M.optNotPaid)},
  ]

  const selectedAdvisorPaid =
    filters.advisorPaid === true
      ? advisorPaidOptions[1]
      : filters.advisorPaid === false
      ? advisorPaidOptions[2]
      : null

  const advisorChargebackOptions = [
    {value: '', label: allPoliciesLabel},
    {value: 'true', label: intl.formatMessage(M.optChargeback)},
    {value: 'false', label: intl.formatMessage(M.optNoChargeback)},
  ]

  const selectedAdvisorChargeback =
    filters.advisorChargeback === true
      ? advisorChargebackOptions[1]
      : filters.advisorChargeback === false
      ? advisorChargebackOptions[2]
      : null

  const selectedOwnsCar =
    filters.ownsCar === true ? yesNoOptions[1] : filters.ownsCar === false ? yesNoOptions[2] : null

  const selectedOwnsHome =
    filters.ownsHome === true ? yesNoOptions[1] : filters.ownsHome === false ? yesNoOptions[2] : null

  const selectedHasAutoPolicy =
    filters.hasAutoPolicy === true
      ? yesNoOptions[1]
      : filters.hasAutoPolicy === false
      ? yesNoOptions[2]
      : null

  const selectedHasHomePolicy =
    filters.hasHomePolicy === true
      ? yesNoOptions[1]
      : filters.hasHomePolicy === false
      ? yesNoOptions[2]
      : null

  const selectedHasCarrierAccount =
    filters.hasCarrierAccount === true
      ? yesNoOptions[1]
      : filters.hasCarrierAccount === false
      ? yesNoOptions[2]
      : null

  const selectedAccountCreatedByClient =
    filters.accountCreatedByClient === true
      ? yesNoOptions[1]
      : filters.accountCreatedByClient === false
      ? yesNoOptions[2]
      : null

  // === POLICY TYPES (agrupados + planos para AppliedFiltersList) ===
const policyTypesOptions =
  policyTypes.map((cat) => ({
    label: cat.name,
    options:
      cat.policyTypeSet?.edges?.map(({ node }: any) => ({
        value: parseId(node.id),
        label: node.typePolicy,
        category: cat.name,
      })) || [],
  })) || []

// Versión plana para filtrar o mostrar labels legibles
const allPolicyTypeOptions =
  policyTypesOptions.flatMap((cat) =>
    (cat.options || []).map((opt) => ({
      ...opt,
      label: `${cat.label} › ${opt.label}`,
    }))
  )

  const policyTypeValues = toArray(filters.policyType).map(String)
  const selectedPolicyTypesOptions = policyTypeValues.length
    ? allPolicyTypeOptions.filter((opt) => {
        const fullLabel = String(opt.label)
        const shortLabel = fullLabel.includes('›') ? fullLabel.split('›').pop()?.trim() || fullLabel : fullLabel
        return (
          policyTypeValues.includes(String(opt.value)) ||
          policyTypeValues.includes(fullLabel) ||
          policyTypeValues.includes(shortLabel)
        )
      })
    : null

  const leadSourcesOptions = [
    ...leadSources.map((s) => ({
      value: parseId(s.id),
      label: s.enabled === false ? intl.formatMessage(M.sourceDisabled, {name: s.name}) : s.name,
    })),
  ]

  const leadSourceValues = toArray(filters.leadSource).map(String)
  const selecteLeadSourcesOptions = leadSourceValues.length
    ? leadSourcesOptions.filter((opt) => leadSourceValues.includes(String(opt.value)))
    : null
  

  const signatureStatusOptions = [
    {value: 'Signed', label: intl.formatMessage(M.optSigned)},
    {value: 'Not Signed', label: intl.formatMessage(M.optNotSigned)},
  ]

  const immigrationStatusOptions = immigrationStatuses.map((s) => ({
    value: String(s.value),
    label: immigrationStatusLabel(intl, String(s.value), s.label),
  }))

  const immigrationStatusValues = toArray(filters.immigrationStatus).map(String)
  const selectedImmigrationStatus = immigrationStatusValues.length
    ? immigrationStatusOptions.filter((opt) => immigrationStatusValues.includes(String(opt.value)))
    : null

  const selectedSignatureStatus =
    signatureStatusOptions.find((opt) => opt.value === filters.signatureStatus) || null

  const signatureFormOptions = [
    ...signatureForms.map((s) => ({
      value: String(s.id),
      label: s.name,
    })),
  ]

  const signatureFormValues = toArray(filters.signatureForm).map(String)
  const selectedSignatureForm = signatureFormValues.length
    ? signatureFormOptions.filter((opt) => signatureFormValues.includes(String(opt.value)))
    : null

  // Agent of Record options (agents with NPN)
  const agentOfRecordOptions = agents
    .filter((a) => a && a.id)
    .map((a) => ({
      value: String(parseId(a.id)),
      label: `${a.firstName} ${a.lastName} - ${a.email} ${a.npn ? ` (${a.npn})` : ''}`,
    }))

  const agentOfRecordValues = toArray(filters.agentOfRecord).map(String)
  const selectedAgentOfRecord = agentOfRecordValues.length
    ? agentOfRecordOptions.filter((opt) => agentOfRecordValues.includes(String(opt.value)))
    : null

  const metalLevelOptions = translatedMetalLevels(intl)

  const selectedMetalLevel = filters.metalLevel
    ? metalLevelOptions.find((opt) => opt.value === filters.metalLevel)
    : null

  const savedFilterOptions = savedFilters.map((filter) => ({
    value: filter.id,
    label: filter.name,
  }))

  const selectedSavedFilterOption =
    savedFilterOptions.find((opt) => opt.value === selectedSavedFilterId) || null

  const handleSavedFilterSelect = (selected: any) => {
    if (!selected) {
      setSelectedSavedFilterId(null)
      setSavedFilterName('')
      return
    }

    const match = savedFilters.find((filter) => filter.id === selected.value)
    if (!match) return

    setSelectedSavedFilterId(match.id)
    setSavedFilterName(match.name)
    setFilters(() => ({...match.filters}))
  }

  const handleSaveAndApply = async () => {
    if (onSaveFilter) {
      const saved = await onSaveFilter(savedFilterName, filters)
      if (!saved) return
      setSelectedSavedFilterId(saved.id)
      setSavedFilterName(saved.name)
    }
    handleSubmit()
  }

  const handleDeleteFilter = async () => {
    if (!onDeleteFilter || !selectedSavedFilterId) return
    const confirmed = window.confirm(intl.formatMessage(M.confirmDelete))
    if (!confirmed) return
    const deleted = await onDeleteFilter(selectedSavedFilterId)
    if (!deleted) return
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
  }

  const handleClearFilters = () => {
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
    setFilters(() => ({...INITIAL_POLICY_FILTERS}))
  }

  const handleApplySavedFilter = () => {
    if (!selectedSavedFilterId) return
    onApply(filters)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' dialogClassName='policies-filters-modal'>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: '200px'}}>
            <Spinner animation='border' />
            <span className='ms-2'>{intl.formatMessage(M.loadingOptions)}</span>
          </div>
        )}
        <div className='d-flex align-items-center justify-content-between mb-3 border-bottom pb-2'>
          <Nav
            variant='tabs'
            className='border-bottom-0'
            activeKey={activeTab}
            onSelect={(key) => {
              const nextTab = (key as 'filters' | 'saved') || 'filters'
              setActiveTab(nextTab)
              if (nextTab === 'saved') {
                setSavedTabMode('manage')
              }
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey='filters'>{intl.formatMessage(M.tabFilters)}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='saved'>{intl.formatMessage(M.tabSaved)}</Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab === 'filters' ? (
            <div className='d-flex gap-2'>
              <Button variant='light' className='px-3' onClick={handleClearFilters}>
                {intl.formatMessage(M.clear)}
              </Button>
              <Button
                variant='dark'
                className='px-3'
                onClick={() => goToSavedTab('save')}
              >
                <i className='bi bi-save me-1' /> {intl.formatMessage(M.saveFilter)}
              </Button>
              <Button variant='primary' className='px-3' onClick={handleSubmit}>
                <i className='bi bi-search me-1' /> {intl.formatMessage(M.applyFilters)}
              </Button>
            </div>
          ) : null}
        </div>
        <Form style={{opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto'}}>
          {activeTab === 'saved' ? (
            <>
              <div className='mb-2'>
                <h5 className='mb-1'>{intl.formatMessage(M.manageSaved)}</h5>
                {savedTabMode === 'manage' ? (
                  <div className='text-muted'>{intl.formatMessage(M.manageHint)}</div>
                ) : null}
              </div>
              {savedTabMode === 'save' ? (
                <Row className='mb-3 align-items-end'>
                  <Col md={8}>
                    <Form.Label>{intl.formatMessage(M.filterName)}</Form.Label>
                    <Form.Control
                      ref={savedNameInputRef}
                      value={savedFilterName}
                      placeholder={intl.formatMessage(M.phFilterName)}
                      onChange={(e) => setSavedFilterName(e.target.value)}
                    />
                    <div className='text-muted mt-2'>
                      {intl.formatMessage(M.saveHint)}
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Label>&nbsp;</Form.Label>
                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleSaveAndApply}
                        disabled={!savedFilterName.trim()}
                      >
                        {intl.formatMessage(M.saveAndApply)}
                      </Button>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row className='mb-2 align-items-center'>
                  <Col md={8}>
                    <Form.Label>{intl.formatMessage(M.savedFilters)}</Form.Label>
                    <Select
                      value={selectedSavedFilterOption}
                      options={savedFilterOptions}
                      onChange={handleSavedFilterSelect}
                      isClearable
                      placeholder={intl.formatMessage(M.phSelectSavedFilter)}
                    />
                  </Col>
                  <Col md={4}>
                    <div className='d-flex flex-wrap justify-content-end gap-2'>
                      <Button variant='light' className='px-3' onClick={onClose}>
                        {intl.formatMessage(M.cancel)}
                      </Button>
                      <Button
                        variant='outline-danger'
                        className='px-3'
                        onClick={handleDeleteFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        {intl.formatMessage(M.delete)}
                      </Button>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleApplySavedFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        {intl.formatMessage(M.applySaved)}
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
              <hr />
            </>
          ) : null}
          {activeTab === 'filters' ? (
            <>
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>{intl.formatMessage(M.fAgent)}</Form.Label>
              <MultiSelectWithActions
                value={selectedAgentsOptions}
                options={agentsOptions}
                onChange={(selected) => handleSelectChange('user', selected)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fAssignedTo)}</Form.Label>
              <MultiSelectWithActions
                value={selectedAsignedToOptions}
                options={assignedToOptions}
                onChange={(selected) => handleSelectChange('assignedTo', selected)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fCarrier)}</Form.Label>
              <MultiSelectWithActions
                value={selectedCarrierOptions}
                options={carrierOptions}
                onChange={(selected) => handleSelectChange('carrier', selected)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fState)}</Form.Label>
              <MultiSelectWithActions
                value={selectedStateEUOptions}
                options={stateEUOptions}
                onChange={(selected) => handleSelectChange('stateEU', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fStateJurisdictionType)}</Form.Label>
              <Select
                value={
                  filters.stateJurisdictionType === 'FEDERAL'
                    ? {value: 'FEDERAL', label: intl.formatMessage(M.optFederal)}
                    : filters.stateJurisdictionType === 'STATE'
                    ? {value: 'STATE', label: intl.formatMessage(M.optStateJur)}
                    : null
                }
                options={[
                  {value: '', label: intl.formatMessage(M.optAllStates)},
                  {value: 'FEDERAL', label: intl.formatMessage(M.optFederal)},
                  {value: 'STATE', label: intl.formatMessage(M.optStateJur)},
                ]}
                onChange={(selected) => handleSelectChange('stateJurisdictionType', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phSelectJurisdiction)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fZipCode)}</Form.Label>
              <Form.Control
                value={filters.zipCode || ''}
                type='number'
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fImmigrationStatus)}</Form.Label>
              <MultiSelectWithActions
                value={selectedImmigrationStatus}
                options={immigrationStatusOptions}
                onChange={handleImmigrationStatusChange}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fPolicyStatus)}</Form.Label>
              <MultiSelectWithActions
                value={selectedPolicyStatusOptions}
                options={policyStatusOptions}
                onChange={(selected) => handleSelectChange('status', selected)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fDocumentStatus)}</Form.Label>
              <MultiSelectWithActions
                value={selectedDocumentStatusOptions}
                options={documentStatusOptions}
                onChange={(selected) => handleSelectChange('documentStatus', selected)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fPaymentStatus)}</Form.Label>
              <MultiSelectWithActions
                value={selectedPaymentStatusOptions}
                options={payentStatusOptions}
                onChange={(selected) => handleSelectChange('paymentStatus', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fPolicyTypes)}</Form.Label>
              <MultiSelectWithActions
                value={selectedPolicyTypesOptions}
                options={policyTypesOptions}
                onChange={(selected) => handleSelectChange('policyType', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fUnassignedPolicies)}</Form.Label>
              <Select
                value={
                  filters.unassigned === true
                    ? {value: 'true', label: intl.formatMessage(M.optOnlyUnassigned)}
                    : filters.unassigned === false
                    ? {value: 'false', label: intl.formatMessage(M.optOnlyAssigned)}
                    : null
                }
                options={[
                  {value: '', label: allPoliciesLabel},
                  {value: 'true', label: intl.formatMessage(M.optOnlyUnassigned)},
                  {value: 'false', label: intl.formatMessage(M.optOnlyAssigned)},
                ]}
                onChange={(selected) => handleSelectChange('unassigned', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fMetalLevel)}</Form.Label>
              <Select
                value={selectedMetalLevel}
                options={metalLevelOptions}
                onChange={(selected) => handleSelectChange('metalLevel', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phSelectMetalLevel)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fSource)}</Form.Label>
              <MultiSelectWithActions
                value={selecteLeadSourcesOptions}
                options={leadSourcesOptions}
                onChange={(selected) => handleSelectChange('leadSource', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fGenderHolder)}</Form.Label>
              <Select
                value={
                  filters.holderGender
                    ? genderOptions.find((opt) => opt.value === filters.holderGender) || null
                    : null
                }
                options={genderOptions}
                onChange={(selected) => handleSelectChange('holderGender', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fMemberIdStatus)}</Form.Label>
              <Select
                value={
                  filters.hasMemberId === true
                    ? {value: 'true', label: intl.formatMessage(M.optWithMemberId)}
                    : filters.hasMemberId === false
                    ? {value: 'false', label: intl.formatMessage(M.optWithoutMemberId)}
                    : null
                }
                options={[
                  {value: '', label: allPoliciesLabel},
                  {value: 'true', label: intl.formatMessage(M.optWithMemberId)},
                  {value: 'false', label: intl.formatMessage(M.optWithoutMemberId)},
                ]}
                onChange={(selected) => handleSelectChange('hasMemberId', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fCommissionReportStatus)}</Form.Label>
              <Select
                value={
                  filters.commissionPayable === true
                    ? {value: 'true', label: intl.formatMessage(M.optConsideredCommission)}
                    : filters.commissionPayable === false
                    ? {value: 'false', label: intl.formatMessage(M.optNotConsideredCommission)}
                    : null
                }
                options={[
                  {value: '', label: allPoliciesLabel},
                  {value: 'true', label: intl.formatMessage(M.optConsideredCommission)},
                  {value: 'false', label: intl.formatMessage(M.optNotConsideredCommission)},
                ]}
                onChange={(selected) => handleSelectChange('commissionPayable', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fCommissionPaymentStatus)}</Form.Label>
              <Select
                value={
                  filters.commissionPaymentStatus
                    ? [
                        {value: 'NEVER_STARTED', label: intl.formatMessage(M.segNeverStarted)},
                        {value: 'CANCELED', label: intl.formatMessage(M.segCanceled)},
                        {value: 'SIN_COMISION', label: intl.formatMessage(M.segSinComision)},
                        {value: 'STAND_BY', label: intl.formatMessage(M.segStandBy)},
                      ].find((option) => option.value === filters.commissionPaymentStatus) || null
                    : null
                }
                options={[
                  {value: '', label: allPoliciesLabel},
                  {value: 'NEVER_STARTED', label: intl.formatMessage(M.segNeverStarted)},
                  {value: 'CANCELED', label: intl.formatMessage(M.segCanceled)},
                  {value: 'SIN_COMISION', label: intl.formatMessage(M.segSinComision)},
                  {value: 'STAND_BY', label: intl.formatMessage(M.segStandBy)},
                ]}
                onChange={(selected) => handleSelectChange('commissionPaymentStatus', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fOwnsCar)}</Form.Label>
              <Select
                value={selectedOwnsCar}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('ownsCar', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fOwnsHome)}</Form.Label>
              <Select
                value={selectedOwnsHome}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('ownsHome', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fHasAutoPolicy)}</Form.Label>
              <Select
                value={selectedHasAutoPolicy}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('hasAutoPolicy', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fHasHomePolicy)}</Form.Label>
              <Select
                value={selectedHasHomePolicy}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('hasHomePolicy', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fHasCarrierAccount)}</Form.Label>
              <Select
                value={selectedHasCarrierAccount}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('hasCarrierAccount', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>{intl.formatMessage(M.fAccountCreatedByClient)}</Form.Label>
              <Select
                value={selectedAccountCreatedByClient}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('accountCreatedByClient', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col xs={12} md={3} lg={3} xl={3}>
              <Form.Label>{intl.formatMessage(M.fActiveAcaCoverage)}</Form.Label>
              <Select
                value={
                  filters.withoutActiveAcaPolicy === true
                    ? {value: 'true', label: intl.formatMessage(M.optWithoutActiveAca)}
                    : filters.withoutActiveAcaPolicy === false
                    ? {value: 'false', label: intl.formatMessage(M.optWithActiveAca)}
                    : null
                }
                options={[
                  {value: 'true', label: intl.formatMessage(M.optWithoutActiveAca)},
                  {value: 'false', label: intl.formatMessage(M.optWithActiveAca)},
                ]}
                onChange={(selected) => handleSelectChange('withoutActiveAcaPolicy', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phAny)}
              />
            </Col>
            <Col xs={12} md={3} lg={3} xl={3}>
              <Form.Label>{intl.formatMessage(M.fActiveMonths)}</Form.Label>
              <div className='d-flex gap-2'>
                <Form.Control
                  type='number'
                  min={0}
                  step={0.5}
                  placeholder={intl.formatMessage(M.phActiveMin)}
                  value={filters.activeMonthsMin ?? ''}
                  onChange={(e) => handleActiveMonthsChange('activeMonthsMin', e.target.value)}
                />
                <Form.Control
                  type='number'
                  min={0}
                  step={0.5}
                  placeholder={intl.formatMessage(M.phActiveMax)}
                  value={filters.activeMonthsMax ?? ''}
                  onChange={(e) => handleActiveMonthsChange('activeMonthsMax', e.target.value)}
                />
              </div>
              <Form.Text className='text-muted'>{intl.formatMessage(M.activeMonthsHelp)}</Form.Text>
            </Col>
          </Row>
          <hr />
          <Row className='mb-3 g-3'>
            <Col xs={12} md={6} xl>
              <DateInput
                id='effectiveDateAfter'
                name='effectiveDateAfter'
                label={intl.formatMessage(M.dEffectiveFrom)}
                value={filters.effectiveDateAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='createdAfter'
                name='createdAfter'
                label={intl.formatMessage(M.dCreatedFrom)}
                value={filters.createdAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='paymentStatusDateAfter'
                name='paymentStatusDateAfter'
                label={intl.formatMessage(M.dPaymentStatusFrom)}
                value={filters.paymentStatusDateAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='canceledAfter'
                name='canceledAfter'
                label={intl.formatMessage(M.dCancellationFrom)}
                value={filters.canceledAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='documentsDueDateAfter'
                name='documentsDueDateAfter'
                label={intl.formatMessage(M.dDocumentsDueFrom)}
                value={filters.documentsDueDateAfter}
                onChange={handleInputChange}
              />
            </Col>
          </Row>
          <Row className='mb-3 g-3'>
            <Col xs={12} md={6} xl>
              <DateInput
                id='effectiveDateBefore'
                name='effectiveDateBefore'
                label={intl.formatMessage(M.dEffectiveTo)}
                value={filters.effectiveDateBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='createdBefore'
                name='createdBefore'
                label={intl.formatMessage(M.dCreatedTo)}
                value={filters.createdBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='paymentStatusDateBefore'
                name='paymentStatusDateBefore'
                label={intl.formatMessage(M.dPaymentStatusTo)}
                value={filters.paymentStatusDateBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='canceledBefore'
                name='canceledBefore'
                label={intl.formatMessage(M.dCancellationTo)}
                value={filters.canceledBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='documentsDueDateBefore'
                name='documentsDueDateBefore'
                label={intl.formatMessage(M.dDocumentsDueTo)}
                value={filters.documentsDueDateBefore}
                onChange={handleInputChange}
              />
            </Col>
          </Row>
          <hr />
          <h5>{intl.formatMessage(M.hSignatureForm)}</h5>
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-2 row-cols-xl-3'>
            <Col>
              <Form.Label>{intl.formatMessage(M.fSignatureFormStatus)}</Form.Label>
              <Select
                value={selectedSignatureStatus}
                options={signatureStatusOptions}
                onChange={(selected) => handleSelectChange('signatureStatus', selected)}
                                isClearable
                                placeholder={intl.formatMessage(M.phSelectOption)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fSignatureForm)}</Form.Label>
              <MultiSelectWithActions
                value={selectedSignatureForm}
                options={signatureFormOptions}
                onChange={(selected) => handleSelectChange('signatureForm', selected)}
              />
            </Col>
            <Col>
              <Form.Label>{intl.formatMessage(M.fAgentNpn)}</Form.Label>
              <MultiSelectWithActions
                value={selectedAgentOfRecord}
                options={agentOfRecordOptions}
                onChange={(selected) => handleSelectChange('agentOfRecord', selected)}
              />
            </Col>
          </Row>
          <hr />
          <h5>{intl.formatMessage(M.hCarrierImport)}</h5>
          <Row className='mb-3 g-2 align-items-end'>
            <Col xs={12} md={6} xl={2}>
              <Form.Label>{intl.formatMessage(M.fCarrierStatus)}</Form.Label>
              <Select
                value={
                  filters.carrierStatusGeneric
                    ? (Array.isArray(filters.carrierStatusGeneric)
                        ? filters.carrierStatusGeneric
                        : [filters.carrierStatusGeneric]
                      ).map((v) => ({value: v, label: carrierStatusLabel(v)}))
                    : null
                }
                options={[
                  {value: 'IN_FORCE', label: intl.formatMessage(M.csInForce)},
                  {value: 'TERMINATED', label: intl.formatMessage(M.csTerminated)},
                  {value: 'GRACE_PERIOD', label: intl.formatMessage(M.csGracePeriod)},
                  {value: 'PENDING_EFFECTUATION', label: intl.formatMessage(M.csPendingEffectuation)},
                  {value: 'PAST_DUE', label: intl.formatMessage(M.csPastDue)},
                  {value: 'DELINQUENT', label: intl.formatMessage(M.csDelinquent)},
                  {value: 'FUTURE_EFFECTIVE', label: intl.formatMessage(M.csFutureEffective)},
                  {value: 'UNKNOWN', label: intl.formatMessage(M.csUnknown)},
                ]}
                onChange={(selected) => handleSelectChange('carrierStatusGeneric', selected)}
                isMulti
                isClearable
                placeholder={intl.formatMessage(M.phSelectCarrierStatus)}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateAfter'
                name='carrierLastAsOfDateAfter'
                label={intl.formatMessage(M.dCarrierAsOfFrom)}
                value={filters.carrierLastAsOfDateAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateBefore'
                name='carrierLastAsOfDateBefore'
                label={intl.formatMessage(M.dCarrierAsOfTo)}
                value={filters.carrierLastAsOfDateBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small'>{intl.formatMessage(M.fAgeIn)}</Form.Label>
              <Select
                value={
                  filters.carrierIsAgeIn === true ? {value: 'true', label: yesLabel}
                  : filters.carrierIsAgeIn === false ? {value: 'false', label: noLabel}
                  : null
                }
                options={[
                  {value: 'true', label: yesLabel},
                  {value: 'false', label: noLabel},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsAgeIn', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phAny)}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small'>{intl.formatMessage(M.fDelinquent)}</Form.Label>
              <Select
                value={
                  filters.carrierIsDelinquent === true ? {value: 'true', label: yesLabel}
                  : filters.carrierIsDelinquent === false ? {value: 'false', label: noLabel}
                  : null
                }
                options={[
                  {value: 'true', label: yesLabel},
                  {value: 'false', label: noLabel},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsDelinquent', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phAny)}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small'>{intl.formatMessage(M.fUnpaidPending)}</Form.Label>
              <Select
                value={
                  filters.carrierIsUnpaidPending === true ? {value: 'true', label: yesLabel}
                  : filters.carrierIsUnpaidPending === false ? {value: 'false', label: noLabel}
                  : null
                }
                options={[
                  {value: 'true', label: yesLabel},
                  {value: 'false', label: noLabel},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsUnpaidPending', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phAny)}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small'>{intl.formatMessage(M.fUnpaidPastDue)}</Form.Label>
              <Select
                value={
                  filters.carrierIsUnpaidPastDue === true ? {value: 'true', label: yesLabel}
                  : filters.carrierIsUnpaidPastDue === false ? {value: 'false', label: noLabel}
                  : null
                }
                options={[
                  {value: 'true', label: yesLabel},
                  {value: 'false', label: noLabel},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsUnpaidPastDue', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phAny)}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small'>{intl.formatMessage(M.fNoCarrierImport)}</Form.Label>
              <Select
                value={
                  filters.carrierNoImportData === true ? {value: 'true', label: yesLabel}
                  : filters.carrierNoImportData === false ? {value: 'false', label: noLabel}
                  : null
                }
                options={[
                  {value: 'true', label: yesLabel},
                  {value: 'false', label: noLabel},
                ]}
                onChange={(selected) => handleSelectChange('carrierNoImportData', selected)}
                isClearable
                placeholder={intl.formatMessage(M.phAny)}
              />
            </Col>
          </Row>

          <>
            <hr />
            <h5>{intl.formatMessage(M.hAcaVerification)}</h5>
            <Row className='mb-3 g-3 row-cols-1 row-cols-md-2'>
              <Col>
                <Form.Label>{intl.formatMessage(M.fAcaResult)}</Form.Label>
                <Select
                  isMulti
                  isClearable
                  placeholder={intl.formatMessage(M.phAnyResult)}
                  value={(() => {
                    const ACA_RESULT_LABELS: Record<string, string> = {
                      green: intl.formatMessage(M.acaGreen),
                      yellow: intl.formatMessage(M.acaYellow),
                      red: intl.formatMessage(M.acaRed),
                      blue: intl.formatMessage(M.acaBlue),
                    }
                    const vals = Array.isArray(filters.acaResult)
                      ? filters.acaResult
                      : filters.acaResult
                      ? [filters.acaResult]
                      : []
                    return vals.map((v) => ({value: v, label: ACA_RESULT_LABELS[v] ?? v}))
                  })()}
                  options={[
                    {value: 'green',  label: intl.formatMessage(M.acaGreen)},
                    {value: 'yellow', label: intl.formatMessage(M.acaYellow)},
                    {value: 'red',    label: intl.formatMessage(M.acaRed)},
                    {value: 'blue',   label: intl.formatMessage(M.acaBlue)},
                  ]}
                  onChange={(selected) => handleSelectChange('acaResult', selected)}
                />
              </Col>
              <Col>
                <Form.Label>{intl.formatMessage(M.fAcaValidity)}</Form.Label>
                <Select
                  isClearable
                  placeholder={intl.formatMessage(M.phAnyStatus)}
                  value={
                    [
                      {value: 'active',   label: intl.formatMessage(M.acaVal28)},
                      {value: 'days_21',  label: intl.formatMessage(M.acaVal21)},
                      {value: 'days_14',  label: intl.formatMessage(M.acaVal14)},
                      {value: 'days_7',   label: intl.formatMessage(M.acaVal7)},
                      {value: 'days_3',   label: intl.formatMessage(M.acaVal3)},
                      {value: 'none',     label: intl.formatMessage(M.acaValNone)},
                    ].find((o) => o.value === filters.acaValidity) ?? null
                  }
                  options={[
                    {value: 'active',  label: intl.formatMessage(M.acaVal28)},
                    {value: 'days_21', label: intl.formatMessage(M.acaVal21)},
                    {value: 'days_14', label: intl.formatMessage(M.acaVal14)},
                    {value: 'days_7',  label: intl.formatMessage(M.acaVal7)},
                    {value: 'days_3',  label: intl.formatMessage(M.acaVal3)},
                    {value: 'none',    label: intl.formatMessage(M.acaValNone)},
                  ]}
                  onChange={(selected) => handleSelectChange('acaValidity', selected)}
                />
              </Col>
            </Row>
          </>

          {!isAgent && (
            <>
              <hr />
              <h5>{intl.formatMessage(M.hAdvisorPayment)}</h5>
              <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
                <Col>
                  <Form.Label>{intl.formatMessage(M.fAdvisorPaymentStatus)}</Form.Label>
                  <Select
                    value={selectedAdvisorPaid}
                    options={advisorPaidOptions}
                    onChange={(selected) => handleSelectChange('advisorPaid', selected)}
                                        isClearable
                                        placeholder={intl.formatMessage(M.phSelectOption)}
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorPaidDateAfter'
                    name='advisorPaidDateAfter'
                    label={intl.formatMessage(M.dAdvisorPaidFrom)}
                    value={filters.advisorPaidDateAfter}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorPaidDateBefore'
                    name='advisorPaidDateBefore'
                    label={intl.formatMessage(M.dAdvisorPaidTo)}
                    value={filters.advisorPaidDateBefore}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
              <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
                <Col>
                  <Form.Label>{intl.formatMessage(M.fChargebackStatus)}</Form.Label>
                  <Select
                    value={selectedAdvisorChargeback}
                    options={advisorChargebackOptions}
                    onChange={(selected) => handleSelectChange('advisorChargeback', selected)}
                                        isClearable
                                        placeholder={intl.formatMessage(M.phSelectOption)}
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorChargebackDateAfter'
                    name='advisorChargebackDateAfter'
                    label={intl.formatMessage(M.dChargebackFrom)}
                    value={filters.advisorChargebackDateAfter}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorChargebackDateBefore'
                    name='advisorChargebackDateBefore'
                    label={intl.formatMessage(M.dChargebackTo)}
                    value={filters.advisorChargebackDateBefore}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </>
          )}
            </>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {activeTab === 'filters' ? (
          <>
            <Button variant='light' className='px-4' onClick={onClose}>
              {intl.formatMessage(M.cancel)}
            </Button>
            <Button variant='dark' className='px-4' onClick={handleSubmit}>
              <i className='bi bi-search me-1' /> {intl.formatMessage(M.applyFilters)}
            </Button>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  )
}
